/** AI editing over MCP — the console's review side (backend metaplan A8; M24 rev 3, AIE §10).
 *
 *  A production tenant's MCP `apply` / `revert` is a **pending change**: nothing changes until a
 *  person holding `console_notification_mcp_write` applies it here (the same permission as the
 *  ops tool). Elicitation in the MCP client never stands in for that person.
 *
 *  - `GET /ai_changes/proposals/[{id}/]` — an MCP proposal's review page: the request, its items,
 *    the preview the requester saw, its pending changes, the change sets it produced.
 *  - `GET /ai_changes/proposals/{id}/renders/` — the proposal on REAL bookings, before → after (the
 *    only place real-booking renders exist; computed per request).
 *  - `GET /ai_changes/pending/[{id}/]`, `POST …/apply/`, `POST …/reject/` — the human decision.
 *  - `GET / PATCH /tour_operators/{id}/ai_edit/` — a tenant's MCP opt-in and MCP auto-apply
 *    (`console_ai_changes_revert`; auto-apply needs an MFA-capable dashboard login). */
import { apiGet, apiPatch, apiPost } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type AIPendingChange = Schemas['AIPendingChange'];
export type AIPendingChangeApplyResult = Schemas['AIPendingChangeApplyResult'];
export type AIPendingChangeDecision = Schemas['AIPendingChangeDecision'];
export type AIProposalListItem = Schemas['AIProposalListItem'];
export type AIProposalReview = Schemas['AIProposalReview'];
export type AIProposalRenders = Schemas['AIProposalRenders'];
export type AIProposalRenderChannel = Schemas['AIProposalRenderChannel'];
/** A franchise licensee's copy of its parent's notifications, as the preview recorded it (P7). */
export type AIProposalFranchiseSummary = Schemas['AIProposalFranchiseSummary'];
/** A notification's settings change, as the preview recorded it (notification-settings P3). */
export type AIProposalSettingsEntry = Schemas['AIProposalSettingsEntry'];
export type AIProposalSettingsDiffRow = Schemas['AIProposalSettingsDiffRow'];
export type AIProposalSettingsChannel = Schemas['AIProposalSettingsChannel'];
export type AIProposalSettingsFranchise = Schemas['AIProposalSettingsFranchise'];
/** A stored review summary, self-describing (client MCP WPS P3-16): its `domain` and record
 *  `sections` (`ReviewSummarySections` renders them generically). */
export type AIProposalPreviewSummary = Schemas['AIProposalPreviewSummary'];
export type AIReviewSection = Schemas['AIReviewSection'];
/** One record of a content change, as the preview recorded it. */
export type AIContentRecordSummary = Schemas['AIContentRecordSummary'];
/** One record a content batch revert reverts. */
export type AIRevertRootLine = Schemas['AIRevertRootLine'];
export type TourOperatorAIEditSettings = Schemas['TourOperatorAIEditSettings'];
export type TourOperatorAIEditSettingsPatch = Schemas['TourOperatorAIEditSettingsPatch'];

/** A real-booking render can take a while (the engine runs the proposal in a rolled-back
 *  transaction, then renders each booking twice). */
export const RENDERS_HTTP_TIMEOUT_MS = 120_000;

export interface PendingFilters {
  status?: string;
  kind?: 'apply' | 'revert';
  tour_operator_id?: number;
}

function toQuery(params: Record<string, string | number | boolean | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export function listProposals(params: { pending?: boolean; tour_operator_id?: number } = {}) {
  return apiGet<Schemas['AIProposalList']>(`/ai_changes/proposals/${toQuery(params)}`);
}

export function retrieveProposal(id: number): Promise<AIProposalReview> {
  return apiGet(`/ai_changes/proposals/${id}/`);
}

export function proposalRenders(id: number): Promise<AIProposalRenders> {
  return apiGet(`/ai_changes/proposals/${id}/renders/`, { timeout: RENDERS_HTTP_TIMEOUT_MS });
}

export function listPending(params: PendingFilters = {}) {
  return apiGet<Schemas['AIPendingChangeList']>(`/ai_changes/pending/${toQuery({ ...params })}`);
}

/** One pending change — a batch revert's review page (it has no change set of its own). */
export function retrievePending(id: string): Promise<AIPendingChange> {
  return apiGet(`/ai_changes/pending/${encodeURIComponent(id)}/`);
}

export function applyPending(
  id: string,
  body: AIPendingChangeDecision = {},
): Promise<AIPendingChangeApplyResult> {
  return apiPost(`/ai_changes/pending/${encodeURIComponent(id)}/apply/`, body, {
    timeout: RENDERS_HTTP_TIMEOUT_MS,
  });
}

export function rejectPending(
  id: string,
  body: AIPendingChangeDecision = {},
): Promise<AIPendingChange> {
  return apiPost(`/ai_changes/pending/${encodeURIComponent(id)}/reject/`, body);
}

export function aiEditSettings(topId: number): Promise<TourOperatorAIEditSettings> {
  return apiGet(`/tour_operators/${topId}/ai_edit/`);
}

export function updateAIEditSettings(
  topId: number,
  patch: TourOperatorAIEditSettingsPatch,
): Promise<TourOperatorAIEditSettings> {
  return apiPatch(`/tour_operators/${topId}/ai_edit/`, patch);
}

export const aiEditKeys = {
  all: ['aiEdit'] as const,
  proposals: (pendingOnly: boolean) => ['aiEdit', 'proposals', pendingOnly] as const,
  proposal: (id: number) => ['aiEdit', 'proposal', id] as const,
  renders: (id: number) => ['aiEdit', 'renders', id] as const,
  pending: (filters: PendingFilters) => ['aiEdit', 'pending', filters] as const,
  pendingOne: (id: string) => ['aiEdit', 'pendingOne', id] as const,
  settings: (topId: number) => ['aiEdit', 'settings', topId] as const,
};

type ChipColor = 'default' | 'info' | 'success' | 'warning' | 'error';

const PENDING_STATUS: Record<AIPendingChange['status'], { label: string; color: ChipColor }> = {
  pending: { label: 'Waiting for a person', color: 'warning' },
  applying: { label: 'Applying', color: 'info' },
  applied: { label: 'Applied', color: 'success' },
  rejected: { label: 'Rejected', color: 'default' },
  failed: { label: 'Failed', color: 'error' },
  stale: { label: 'Out of date', color: 'default' },
  superseded: { label: 'Superseded', color: 'default' },
};

export function pendingStatusLabel(status: AIPendingChange['status']): string {
  return PENDING_STATUS[status]?.label ?? status;
}

export function pendingStatusColor(status: AIPendingChange['status']): ChipColor {
  return PENDING_STATUS[status]?.color ?? 'default';
}

/** Why an open pending change offers this viewer no Apply / Reject (`can_decide` false). */
export const CANNOT_DECIDE_TEXT =
  'Applying or rejecting it needs console_notification_mcp_write and the console write permission of every kind of record it touches (console_regions_manage for regions, console_ai_vocabulary_manage for the AI vocabulary, …).';

/** The words for an error body of the decision endpoints. */
export function decisionErrorText(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'stale':
      return 'The notifications changed since this was previewed: it can’t be applied as reviewed. The requester has to preview and apply again.';
    case 'not_open':
      return 'Someone already decided this change.';
    case 'change_sets_disabled':
      return 'Change sets are not enabled yet (CHANGE_SETS): AI changes cannot be applied.';
    default:
      return fallback;
  }
}
